/* ==========================================================================
   NOBO — Étapes épinglées, canvas dessinés et apparitions mot par mot
   - UN seul gestionnaire de défilement (throttlé par requestAnimationFrame) écrit
     des propriétés CSS sur chaque étape (.nobo-stage) : hero, rail, process, statement.
   - Les canvas ne s'animent que lorsqu'ils sont visibles.
   - Titres et textes [data-split] découpés en mots ; éléments [data-rev] révélés
     par un IntersectionObserver (seuil 0.12, désobservés à l'entrée).
   - Rien ne bouge si « réduire les animations » est activé ou dans l'éditeur.
   JS natif, sans dépendance.
   ========================================================================== */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = root.classList.contains('shopify-design-mode');
  const motion = !reduceMotion && !designMode;
  const ARABIC = /[؀-ۿ]/;
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  if (motion) root.classList.add('nobo-motion');

  /* ------------------------------------------------------------------
     Texte en écriture arabe : pas d'espacement de lettres (liaisons)
     ------------------------------------------------------------------ */
  document.querySelectorAll('[data-nobo-mono], .nobo-stage__wordmark, .nobo-stage [data-split]').forEach((el) => {
    if (ARABIC.test(el.textContent)) el.classList.add('is-arabic');
  });

  /* ------------------------------------------------------------------
     Découpage mot par mot (préserve <em>, <strong>, liens…)
     ------------------------------------------------------------------ */
  const splitWords = (el) => {
    if (el.dataset.splitDone) return;
    let index = 0;
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const parts = child.textContent.split(/(\s+)/);
          const fragment = document.createDocumentFragment();
          parts.forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              fragment.append(part);
              return;
            }
            const word = document.createElement('span');
            word.className = 'nobo-w';
            word.style.setProperty('--i', index++);
            word.textContent = part;
            fragment.append(word);
          });
          child.replaceWith(fragment);
        } else if (child.nodeType === Node.ELEMENT_NODE && !['SCRIPT', 'STYLE', 'BR', 'SVG'].includes(child.tagName)) {
          walk(child);
        }
      });
    };
    walk(el);
    el.dataset.splitDone = 'true';
    el.classList.add('nobo-split');
  };

  /* ------------------------------------------------------------------
     Apparitions : un seul IntersectionObserver
     ------------------------------------------------------------------ */
  const revealObserver = motion
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.12 }
      )
    : null;

  const prepareReveals = () => {
    if (!motion) return;

    // Titres, accroches et colonnes : mot par mot
    const splitTargets = new Set([
      ...document.querySelectorAll('[data-split]'),
      ...document.querySelectorAll('#MainContent .text-block :is(h1, h2)'),
    ]);
    splitTargets.forEach((el) => {
      if (el.closest('[data-nosplit]')) return;
      splitWords(el);
      revealObserver.observe(el);
    });

    // Groupes d'éléments décalés : chaque enfant reçoit son propre délai
    const groups = [
      ['.nobo-footer__links', ':scope > li', 60],
      ['.nobo-footer__columns', ':scope > *', 90],
      ['.nobo-footer__bottom', ':scope > *', 70],
      ['#MainContent .accordion', ':scope accordion-custom, :scope .details', 60],
      ['#MainContent .shopify-section[id$="__nobo_reassurance"] .section-content-wrapper', ':scope > .group-block', 80],
    ];
    groups.forEach(([containerSelector, childSelector, step]) => {
      document.querySelectorAll(containerSelector).forEach((container) => {
        container.querySelectorAll(childSelector).forEach((child, i) => {
          if (child.hasAttribute('data-rev')) return;
          child.setAttribute('data-rev', '');
          child.style.setProperty('--d', `${i * step}ms`);
        });
      });
    });

    // Éléments marqués dans le Liquid des étapes NOBO
    document.querySelectorAll('[data-rev]').forEach((el) => revealObserver.observe(el));
  };

  /* ------------------------------------------------------------------
     Canvas : boucle d'animation unique, seulement pour les canvas visibles
     ------------------------------------------------------------------ */
  const canvases = new Map(); // canvas -> { draw, visible, ctx, w, h }

  const sizeCanvas = (canvas, state) => {
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    state.w = Math.max(1, rect.width);
    state.h = Math.max(1, rect.height);
    canvas.width = Math.round(state.w * ratio);
    canvas.height = Math.round(state.h * ratio);
    state.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  };

  const canvasObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const state = canvases.get(entry.target);
      if (state) state.visible = entry.isIntersecting;
    });
    startLoop();
  });

  const registerCanvas = (canvas, draw) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const state = { draw, ctx, visible: false, w: 0, h: 0 };
    canvases.set(canvas, state);
    sizeCanvas(canvas, state);
    canvasObserver.observe(canvas);
    return state;
  };

  const cssColor = (el, name, fallback) => getComputedStyle(el).getPropertyValue(name).trim() || fallback;

  let loopId = null;
  const loop = (time) => {
    let anyVisible = false;
    canvases.forEach((state, canvas) => {
      if (!state.visible) return;
      anyVisible = true;
      state.ctx.clearRect(0, 0, state.w, state.h);
      state.draw(state.ctx, state.w, state.h, time, canvas);
    });
    loopId = anyVisible ? requestAnimationFrame(loop) : null;
  };
  const startLoop = () => {
    if (loopId === null) loopId = requestAnimationFrame(loop);
  };
  const drawOnce = () => {
    canvases.forEach((state, canvas) => {
      state.ctx.clearRect(0, 0, state.w, state.h);
      state.draw(state.ctx, state.w, state.h, 0, canvas);
    });
  };

  /* Fils de soie (hero) et lignes de contour (plaque) */
  const drawContours = (lineCount, accentEvery, amplitude) => (ctx, w, h, time, canvas) => {
    const ink = cssColor(canvas, '--line-color', 'rgba(251,248,243,.075)');
    const accent = cssColor(canvas, '--line-accent', 'rgba(205,184,156,.34)');
    const t = motion ? time : 0;
    ctx.lineWidth = 1;
    for (let i = 0; i < lineCount; i++) {
      const baseY = ((i + 0.5) / lineCount) * h;
      ctx.strokeStyle = (i + 1) % accentEvery === 0 ? accent : ink;
      ctx.beginPath();
      for (let x = 0; x <= w + 12; x += 12) {
        const y =
          baseY +
          Math.sin(x * 0.0042 + i * 0.37 + t * 0.00022) * h * 0.011 * amplitude +
          Math.sin(x * 0.0011 - t * 0.00009 + i * 0.9) * h * 0.022 * amplitude;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
  };

  /* Diagrammes du process, redessinés entièrement selon l'étape */
  const diagrams = {
    rings(ctx, s, t, ink, accent) {
      const c = s / 2;
      for (let k = 0; k < 6; k++) {
        const r = ((k + 1) / 7) * c * 0.92 * (1 + 0.045 * Math.sin(t * 0.002 + k * 0.8));
        ctx.strokeStyle = k === 5 ? accent : ink;
        ctx.beginPath();
        ctx.arc(c, c, r, 0, Math.PI * 2);
        ctx.stroke();
      }
    },
    spiral(ctx, s, t, ink, accent) {
      const c = s / 2;
      const total = 38;
      const lit = Math.floor(t / 420) % total;
      for (let q = 0; q < total; q++) {
        const angle = q * 2.399;
        const radius = c * 0.86 * Math.sqrt((q + 0.5) / total);
        const isLit = q === lit;
        ctx.fillStyle = isLit ? accent : ink;
        ctx.beginPath();
        ctx.arc(c + Math.cos(angle) * radius, c + Math.sin(angle) * radius, isLit ? 5 : 2.2, 0, Math.PI * 2);
        ctx.fill();
      }
    },
    grid(ctx, s, t, ink, accent) {
      const pad = s * 0.12;
      const cell = (s - pad * 2) / 5;
      const keep = 17;
      for (let n = 0; n < 25; n++) {
        const x = pad + (n % 5) * cell;
        const y = pad + Math.floor(n / 5) * cell;
        ctx.strokeStyle = n === keep ? accent : ink;
        ctx.strokeRect(x + 3, y + 3, cell - 6, cell - 6);
        if (n !== keep) {
          ctx.beginPath();
          ctx.moveTo(x + 3, y + cell - 3);
          ctx.lineTo(x + cell - 3, y + 3);
          ctx.stroke();
        }
      }
    },
    square(ctx, s, t, ink, accent) {
      const c = s / 2;
      const side = s * 0.42;
      ctx.strokeStyle = ink;
      ctx.strokeRect(c - side / 2, c - side / 2, side, side);
      ctx.fillStyle = accent;
      ctx.beginPath();
      ctx.arc(c, c, 4 + 3 * (0.5 + 0.5 * Math.sin(t * 0.004)), 0, Math.PI * 2);
      ctx.fill();
    },
  };

  /* ------------------------------------------------------------------
     Étapes épinglées
     ------------------------------------------------------------------ */
  const stages = [...document.querySelectorAll('[data-nobo-stage]')].map((el) => ({
    el,
    type: el.dataset.noboStage,
    pin: el.querySelector('.nobo-stage__pin'),
    track: el.querySelector('[data-rail-track]'),
    railOverflow: 0,
    stepIndex: -1,
    steps: [...el.querySelectorAll('[data-step]')],
    numeral: el.querySelector('[data-step-numeral]'),
    diagram: el.querySelector('[data-diagram]'),
  }));

  const measure = () => {
    stages.forEach((stage) => {
      if (stage.track && stage.pin) {
        stage.railOverflow = Math.max(0, stage.track.scrollWidth - stage.pin.clientWidth);
      }
    });
    canvases.forEach((state, canvas) => sizeCanvas(canvas, state));
    if (!motion) drawOnce();
  };

  const progressOf = (stage) => {
    const rect = stage.el.getBoundingClientRect();
    const pinTop = stage.pin ? parseFloat(getComputedStyle(stage.pin).top) || 0 : 0;
    const total = rect.height - (stage.pin ? stage.pin.offsetHeight : 0);
    if (total <= 0) return 0;
    return clamp((pinTop - rect.top) / total, 0, 1);
  };

  const setStep = (stage, index) => {
    if (index === stage.stepIndex) return;
    stage.stepIndex = index;
    stage.steps.forEach((step, i) => {
      step.classList.toggle('is-lit', i <= index);
      step.classList.toggle('is-current', i === index);
    });
    const current = stage.steps[index];
    if (stage.numeral) stage.numeral.textContent = String(index + 1).padStart(2, '0');
    if (stage.diagram && current) stage.diagram.dataset.mode = current.dataset.diagramMode || 'rings';
  };

  const update = () => {
    stages.forEach((stage) => {
      const p = motion ? progressOf(stage) : 0;
      const style = stage.el.style;
      style.setProperty('--p', p.toFixed(4));

      switch (stage.type) {
        case 'hero':
          style.setProperty('--hero-y', `${(-70 * p).toFixed(2)}px`);
          style.setProperty('--hero-scale', (1 - 0.18 * p).toFixed(4));
          style.setProperty('--hero-alpha', clamp(1 - p * 1.15, 0, 1).toFixed(4));
          style.setProperty('--vignette', (0.35 + 0.55 * p).toFixed(4));
          break;
        case 'rail': {
          const rtl = getComputedStyle(stage.el).direction === 'rtl';
          const x = p * stage.railOverflow * (rtl ? 1 : -1);
          style.setProperty('--rail-x', `${x.toFixed(1)}px`);
          break;
        }
        case 'process':
          if (stage.steps.length) {
            setStep(stage, motion ? Math.min(stage.steps.length - 1, Math.floor(p * stage.steps.length * 0.9999)) : stage.steps.length - 1);
          }
          break;
        case 'statement':
          style.setProperty('--plate-y', `${(-3 + 6 * p).toFixed(3)}%`);
          style.setProperty('--plate-scale', (1.06 - 0.03 * p).toFixed(4));
          break;
      }
    });
  };

  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      update();
    });
  };

  /* ------------------------------------------------------------------
     Démarrage
     ------------------------------------------------------------------ */
  const init = () => {
    stages.forEach((stage) => {
      stage.el.querySelectorAll('canvas[data-canvas]').forEach((canvas) => {
        const kind = canvas.dataset.canvas;
        if (kind === 'threads') registerCanvas(canvas, drawContours(46, 9, 1));
        if (kind === 'plate') registerCanvas(canvas, drawContours(26, 6, 1.4));
        if (kind === 'diagram') {
          registerCanvas(canvas, (ctx, w, h, time, el) => {
            const ink = cssColor(el, '--line-color', 'rgba(251,248,243,.55)');
            const accent = cssColor(el, '--line-accent', '#CDB89C');
            const mode = el.dataset.mode || 'rings';
            ctx.lineWidth = 1;
            (diagrams[mode] || diagrams.rings)(ctx, Math.min(w, h), motion ? time : 0, ink, accent);
          });
        }
      });
    });

    prepareReveals();
    measure();
    update();
    if (!motion) {
      drawOnce();
      return;
    }
    // La page défile dans .page-wrapper sur ordinateur, dans la fenêtre sur mobile :
    // écoute en capture pour couvrir les deux.
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', () => {
      measure();
      onScroll();
    });
    window.addEventListener('load', () => {
      measure();
      onScroll();
    });
    startLoop();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Éditeur de thème : remesurer quand une section est rechargée
  document.addEventListener('shopify:section:load', () => {
    measure();
    update();
  });
})();
